// Dictionary, 867 — the 867-specific segments with no shared equivalent:
// BPT (Beginning Segment for Product Transfer and Resale Report) and PTD
// (Product Transfer Detail).

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import type {CodeListEntry} from './types';
import {TRANSACTION_SET_PURPOSE_CODES} from './transaction-set-purpose-codes';
import {PRODUCT_TRANSFER_TYPE_CODES} from './product-transfer-type-codes';
import {UNIT_OR_BASIS_FOR_MEASUREMENT_CODES} from './unit-or-basis-for-measurement-codes';
import {REFERENCE_IDENTIFICATION_QUALIFIER_CODES} from './reference-identification-qualifiers';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

const REPORT_TYPE_CODES: CodeListEntry[] = [
  {code: '01', label: 'Product Transfer'},
  {code: '02', label: 'Resale'},
  {code: 'SS', label: 'Seller Sales Report'},
  {code: 'TM', label: 'Stock Sale Report'},
  {code: 'TN', label: 'Delayed Sale Report'},
  {code: 'DD', label: 'Distributor Inventory Report'},
  {code: 'CS', label: 'Consigned Inventory Sales Report'},
  {code: 'PR', label: 'Purchase Report'},
  {code: 'ZZ', label: 'Mutually Defined'},
];

export const SEGMENTS_867: Record<string, SegmentDefinition> = {
  BPT: {
    id: 'BPT',
    name: 'Beginning Segment for Product Transfer and Resale Report',
    purpose:
      'Identifies what kind of report this is (original, cancellation, replace, duplicate, re-submission), the report number and date, and the report type (product transfer, resale, seller sales, stock sale, etc.). Required at the start of every 867.',
    loop: LoopId.Header,
    required: true,
    elements: [
      el(
        1,
        'Transaction Set Purpose Code',
        'Whether this is an original report, a cancellation, a replace, a duplicate, or a re-submission.',
        {
          dataType: 'code',
          required: true,
          codes: TRANSACTION_SET_PURPOSE_CODES,
        }
      ),
      el(2, 'Report Number', 'The reporting party’s own report identifier.', {
        required: true,
      }),
      el(3, 'Date', 'Date the report was prepared.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
      el(
        4,
        'Report Type Code',
        'What kind of report this is — a product transfer, a resale, a seller sales report, a stock sale report, etc.',
        {
          dataType: 'code',
          codes: REPORT_TYPE_CODES,
        }
      ),
    ],
  },

  PTD: {
    id: 'PTD',
    name: 'Product Transfer Detail',
    purpose:
      'One occurrence per transferred or resold product line — what kind of transfer/sale this is, the quantity, and an optional reference number. Carries its own nested QTY, AMT, CTP, REF and DTM occurrences.',
    loop: LoopId.Item,
    required: true,
    elements: [
      el(
        1,
        'Product Transfer Type Code',
        'What kind of transfer or resale this line reports — a stock sale, drop ship sale, return, received from vendor, inventory adjustment, etc.',
        {
          dataType: 'code',
          required: true,
          codes: PRODUCT_TRANSFER_TYPE_CODES,
        }
      ),
      el(2, 'Quantity', 'The quantity transferred, sold, or adjusted.', {
        dataType: 'numeric',
        required: true,
      }),
      el(3, 'Unit of Measure Code', 'The unit the quantity is expressed in.', {
        dataType: 'code',
        codes: UNIT_OR_BASIS_FOR_MEASUREMENT_CODES,
      }),
      el(
        4,
        'Reference Identification Qualifier',
        'What kind of reference number follows, when one is given.',
        {
          dataType: 'code',
          codes: REFERENCE_IDENTIFICATION_QUALIFIER_CODES,
        }
      ),
      el(5, 'Reference Identification', 'The reference number itself.'),
    ],
  },
};
